import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PortalSpinner } from 'components/PortalSpinner'
import { ConversationActions } from 'store/actions'
import { useConversationChannel } from 'hooks/useConversationChannel'
import Item from './Item'
import { connect } from 'react-redux'
import toast from 'react-hot-toast'
import { chatDateLabel, isSameDay } from 'utils/helpers'

const enhancer = connect(null, { ...ConversationActions })

const initialParams = {
  page: 1,
  items: 20,
  hasMore: true
}

const messageId = (message) => message?.id || message?.attributes?.id
const messageTime = (message) => message?.attributes?.time

function List({ getMessages, conversationId, sentMessage }) {
  const [messages, setMessages] = useState(null)
  const [params, setParams] = useState(initialParams)
  const [loadingMore, setLoadingMore] = useState(false)
  const scrollRef = useRef(null)

  const renderedMessages = useMemo(
    () => messages ? [...messages].reverse() : [],
    [messages]
  )

  const messageGroups = useMemo(() => {
    const groups: { label: string; firstId: any; messages: any[] }[] = []
    for (const message of renderedMessages) {
      const time = messageTime(message)
      const lastGroup = groups[groups.length - 1]
      const prevTime = lastGroup?.messages.length
        ? messageTime(lastGroup.messages[lastGroup.messages.length - 1])
        : undefined
      if (!lastGroup || (time && !isSameDay(time, prevTime))) {
        groups.push({ label: time ? chatDateLabel(time) : '', firstId: messageId(message), messages: [message] })
      } else {
        lastGroup.messages.push(message)
      }
    }
    return groups
  }, [renderedMessages])

  const scrollToBottom = useCallback(() =>
    requestAnimationFrame(() => {
      if (scrollRef.current)
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }), [])

  const prependMessage = useCallback((msg: any) => {
    setMessages((prev: any[]) => [
      msg,
      ...(prev ?? []).filter((message) => messageId(message) !== messageId(msg)),
    ])
    scrollToBottom()
  }, [scrollToBottom])

  useConversationChannel(conversationId, prependMessage)

  const loadMessages = useCallback(async (nextParams = initialParams) => {
    if (!conversationId) return

    try {
      const { data = [], pagy } = await getMessages(conversationId, nextParams)
      setParams({ ...nextParams, hasMore: !!pagy?.next })

      if (nextParams.page !== 1) {
        const container = scrollRef.current
        const prevHeight = container?.scrollHeight || 0
        setMessages((prev) => [
          ...(prev || []),
          ...data.filter((message) =>
            !(prev || []).some((_message) => messageId(_message) === messageId(message))
          ),
        ])

        requestAnimationFrame(() => {
          if (container)
            container.scrollTop += container.scrollHeight - prevHeight
        })
      } else {
        setMessages(data)
        scrollToBottom()
      }
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load messages')
    }
  }, [conversationId, getMessages, scrollToBottom])

  useEffect(() => {
    setMessages(null)
    setParams(initialParams)
    setLoadingMore(false)
    loadMessages()
  }, [conversationId, loadMessages])

  useEffect(() => {
    if (sentMessage) prependMessage(sentMessage)
  }, [sentMessage, prependMessage])

  const handleScroll = async () => {
    const container = scrollRef.current
    if (!container || loadingMore || !params.hasMore || container.scrollTop > 50) return

    setLoadingMore(true)
    try {
      await loadMessages({ ...params, page: params.page + 1 })
    } finally {
      setLoadingMore(false)
    }
  }

  if (messages === null) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <PortalSpinner size={6} />
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 min-h-0 overflow-y-auto px-4 py-4"
    >
      {loadingMore && (
        <div className="mb-4">
          <PortalSpinner size={6} />
        </div>
      )}

      {messageGroups.map((group) => (
        <div key={`date-group-${group.firstId}`}>
          {group.label && (
            <div className="sticky top-2 z-10 my-3 flex justify-center" style={{ overflowAnchor: 'none' }}>
              <time className="rounded-md bg-secondary-100 px-3 py-1 text-[11px] font-medium text-secondary-500 shadow-sm">
                {group.label}
              </time>
            </div>
          )}
          {group.messages.map((message) => (
            <Item key={messageId(message)} item={message} />
          ))}
        </div>
      ))}
    </div>
  )
}

export default enhancer(List)
